import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, FileCheck, Clock, IndianRupee, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { formatUSD } from '@/utils/formatters';

const ProjectsBills = () => {
  const { user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [search, setSearch] = useState('');
  const [expandedResource, setExpandedResource] = useState(null);

  useEffect(() => { loadData(); }, [period, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects-bills?period=${period}&month=${selectedMonth}`);
      setData(res.data);
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.resources;
    const q = search.toLowerCase();
    return data.resources.filter(r => r.resource.toLowerCase().includes(q));
  }, [data, search]);

  const totals = useMemo(() => {
    if (!filtered.length) return { projects: 0, completed: 0, pending: 0, periodCompleted: 0, amount: 0, periodAmount: 0 };
    return {
      projects: filtered.reduce((s, r) => s + r.total_projects, 0),
      completed: filtered.reduce((s, r) => s + r.completed, 0),
      pending: filtered.reduce((s, r) => s + r.pending, 0),
      periodCompleted: filtered.reduce((s, r) => s + r.period_completed, 0),
      amount: filtered.reduce((s, r) => s + r.total_amount, 0),
      periodAmount: filtered.reduce((s, r) => s + r.period_amount, 0),
    };
  }, [filtered]);

  if (loading && !data) return <DashboardLayout user={user}><div className="flex items-center justify-center h-96"><div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="projects-bills-title">Projects & Bills</h1>
            <p className="text-muted-foreground">Email project status based on due dates</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={selectedMonth} onValueChange={(v) => setSelectedMonth(v)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() - i);
                  const val = d.toISOString().slice(0, 7);
                  return <SelectItem key={val} value={val}>{d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              <Button variant={period === 'weekly' ? 'default' : 'ghost'} size="sm" onClick={() => setPeriod('weekly')}>This Week</Button>
              <Button variant={period === 'monthly' ? 'default' : 'ghost'} size="sm" onClick={() => setPeriod('monthly')}>Full Month</Button>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-xl font-bold text-violet-600">{data?.total_resources || 0}</p>
              <p className="text-[10px] text-muted-foreground">Resources</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-xl font-bold text-blue-600">{totals.projects}</p>
              <p className="text-[10px] text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-xl font-bold text-green-600">{totals.completed}</p>
              <p className="text-[10px] text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-xl font-bold text-amber-600">{totals.pending}</p>
              <p className="text-[10px] text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-xl font-bold text-emerald-600">{totals.periodCompleted}</p>
              <p className="text-[10px] text-muted-foreground">{period === 'weekly' ? 'This Week' : 'This Month'}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-600">
            <CardContent className="pt-3 pb-2 text-center">
              <p className="text-xl font-bold text-green-700">{formatUSD(totals.periodAmount)}</p>
              <p className="text-[10px] text-muted-foreground">Period Revenue</p>
            </CardContent>
          </Card>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email..." className="pl-10" />
        </div>

        {/* Resource cards */}
        <div className="space-y-3">
          {filtered.map(r => (
            <Card key={r.resource} className="overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/30" onClick={() => setExpandedResource(expandedResource === r.resource ? null : r.resource)}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium text-sm">{r.resource}</span>
                  <Badge className="bg-green-100 text-green-700 text-[10px]"><FileCheck className="w-3 h-3 mr-1" />{r.completed}</Badge>
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]"><Clock className="w-3 h-3 mr-1" />{r.pending} pending</Badge>
                  {r.revisions > 0 && <Badge className="bg-purple-100 text-purple-700 text-[10px]">{r.revisions} revisions</Badge>}
                  <span className="text-xs text-muted-foreground">{r.total_minutes} min</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-green-600">{formatUSD(r.total_amount)}</span>
                  <span className="text-xs text-muted-foreground">Period: {r.period_completed}</span>
                  {expandedResource === r.resource ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
              {expandedResource === r.resource && (
                <div className="border-t">
                  <div className="overflow-x-auto max-h-72">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-center text-xs">Status</TableHead>
                          <TableHead className="text-center text-xs">Due Date</TableHead>
                          <TableHead className="text-center text-xs">Title</TableHead>
                          <TableHead className="text-center text-xs">Episode</TableHead>
                          <TableHead className="text-center text-xs">Task</TableHead>
                          <TableHead className="text-center text-xs">QTY</TableHead>
                          <TableHead className="text-center text-xs">Client $</TableHead>
                          <TableHead className="text-center text-xs">Translator ₹</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {r.entries.slice(0, 50).map((e, i) => (
                          <TableRow key={i} className={e.status === 'pending' ? 'bg-amber-50' : e.is_revision ? 'bg-purple-50' : ''}>
                            <TableCell className="text-center">
                              {e.is_revision
                                ? <Badge className="bg-purple-100 text-purple-700 text-[9px]">Revision</Badge>
                                : e.status === 'completed'
                                ? <Badge className="bg-green-100 text-green-700 text-[9px]">Done</Badge>
                                : <Badge className="bg-amber-100 text-amber-700 text-[9px]">Pending</Badge>}
                            </TableCell>
                            <TableCell className="text-center text-xs whitespace-nowrap">{e.due_date || '—'}</TableCell>
                            <TableCell className="text-center text-xs">{e.main_title || '—'}</TableCell>
                            <TableCell className="text-center text-xs">{e.episode_title || '—'}</TableCell>
                            <TableCell className="text-center text-xs">{e.task || '—'}</TableCell>
                            <TableCell className="text-center text-xs">{e.qty || '—'}</TableCell>
                            <TableCell className="text-center text-xs text-green-600">{e.client_amount ? formatUSD(e.client_amount) : '—'}</TableCell>
                            <TableCell className="text-center text-xs">{e.translator_invoice ? `₹${e.translator_invoice}` : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {r.entries.length > 50 && <p className="text-xs text-center text-muted-foreground py-1">Showing 50 of {r.entries.length}</p>}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProjectsBills;
